import React, { useEffect, useState } from 'react';

import { EditableCard } from '@/components/EditableCard';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAccountGroupProfileWithAumIncluded } from '@/views/portfolio/account-aum-queries';
import { useUpdateAccountGroupTaxLossHarvesting } from '@/views/portfolio/account-group-queries';
import { useRequiredParams } from 'utils';

import { supportsTaxLossHarvesting, TAX_LOSS_HARVESTING } from './constants';

interface AccountGroupLevelCardProps {
    className?: string;
}

export const AccountGroupLevelInvestmentStyleCard: React.FC<AccountGroupLevelCardProps> = ({ className }) => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { data: accountGroup } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);
    const { mutate: updateTaxLossHarvesting, isPending: updating } =
        useUpdateAccountGroupTaxLossHarvesting(accountGroupGUID);

    const accounts = accountGroup?.accounts;
    const supportedAccounts = accounts?.filter(supportsTaxLossHarvesting);
    const supportedAccountCount = supportedAccounts?.length ?? 0;
    const enabledForAll =
        supportedAccounts &&
        supportedAccounts.length > 0 &&
        supportedAccounts.every((account) => account.taxLossHarvesting);

    const [isEditing, setIsEditing] = useState(false);
    const [taxLossHarvesting, setTaxLossHarvesting] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        setTaxLossHarvesting(enabledForAll);
    }, [enabledForAll]);

    const handleCancel = () => {
        setIsEditing(false);
        setTaxLossHarvesting(enabledForAll);
    };

    const handleSave = () => {
        updateTaxLossHarvesting(
            { taxLossHarvesting: !!taxLossHarvesting },
            {
                onSuccess: () => {
                    setIsEditing(false);
                },
            },
        );
    };

    return (
        <EditableCard
            className={className}
            title="Portfolio Level Investment Styles"
            editing={isEditing}
            saving={updating}
            onEdit={() => setIsEditing(true)}
            onCancel={handleCancel}
            onSave={handleSave}
        >
            {accounts ? (
                <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                        <Label htmlFor={TAX_LOSS_HARVESTING.key} className="text-sm">
                            {TAX_LOSS_HARVESTING.label}
                        </Label>
                        <Switch
                            className="ml-auto"
                            id={TAX_LOSS_HARVESTING.key}
                            checked={!!taxLossHarvesting}
                            onCheckedChange={setTaxLossHarvesting}
                            disabled={!isEditing || supportedAccountCount === 0}
                        />
                    </div>
                    <div className="text-xs text-gray-500">
                        {supportedAccountCount === 0
                            ? 'No taxable account in this portfolio; tax loss harvesting is not available.'
                            : `Applies to all ${supportedAccountCount} taxable account(s); IRAs and other non-taxable accounts stay off.`}
                    </div>
                </div>
            ) : (
                <div className="text-gray-500">Loading investment styles...</div>
            )}
        </EditableCard>
    );
};
